import type { AuditEventRow, AuditListResponse } from "@lms/shared-types";
import { useCallback, useEffect, useId, useState } from "react";
import { fetchFile, fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, TextField } from "../../components/ui";
import { useAnnounce } from "../../lib/announce";
import { AUDIT_ACTION_OPTIONS, AUTHORITY_TONE, AUTHORITY_WORD, OUTCOME_TONE, OUTCOME_WORD, actionLabel, actorLabel } from "../../lib/audit-labels";

/**
 * The audit trail (REC-14, design.md §16 "Program › Audit trail"): the
 * rows the caller's scope reaches, newest first, with the filters the API
 * has, a "load more" cursor, and the export. States are words, never a
 * colour alone; every action, actor and authority in words (Starsach's
 * page, the owner's direction); a change is "N fields" that opens a
 * dialog with one row per field, before beside after; a withheld change
 * says it was withheld. The sentence on what this trail cannot show is
 * the template's own advice.
 */
const show = (v: unknown): string => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

/** One row per field the change touched: the key in mono, before and after side by side; then the details and the reason. */
function ChangeDialog({ e, onClose }: { e: AuditEventRow; onClose: () => void }) {
  const fields = [...new Set([...Object.keys(e.before ?? {}), ...Object.keys(e.after ?? {})])];
  const details = Object.entries(e.details ?? {});
  return (
    <Dialog title={`${actionLabel(e.action)} — the change`} onClose={onClose} closeLabel="Done">
      <p className="text-muted">
        <code className="font-mono text-xs">{e.action}</code> on {e.resource.kind} <code className="font-mono text-xs [overflow-wrap:anywhere]">{e.resource.id}</code>
      </p>
      {e.reason && (
        <p className="mt-3">
          <span className="font-semibold">Reason:</span> {e.reason}
        </p>
      )}
      {fields.length > 0 && (
        <table className="mt-3 w-full text-sm">
          <caption className="sr-only">Fields changed</caption>
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th scope="col" className="py-2 pr-3 font-medium">Field</th>
              <th scope="col" className="py-2 pr-3 font-medium">Before</th>
              <th scope="col" className="py-2 font-medium">After</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((k) => (
              <tr key={k} className="border-b border-line align-top last:border-0">
                <th scope="row" className="py-2 pr-3 font-mono text-xs font-normal">{k}</th>
                <td className="py-2 pr-3 break-words">{e.before ? show(e.before[k]) : "—"}</td>
                <td className="py-2 break-words">{e.after ? show(e.after[k]) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {details.length > 0 && (
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr] [&>*]:min-w-0">
          {details.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="font-mono text-xs text-muted">{k}</dt>
              <dd className="break-words">{show(v)}</dd>
            </div>
          ))}
        </dl>
      )}
      <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-muted sm:grid-cols-[auto_1fr]">
        <dt>Actor</dt>
        <dd className="break-all">{e.actor.subject}</dd>
        <dt>Request</dt>
        <dd className="break-all">{e.requestId ?? "—"}</dd>
      </dl>
    </Dialog>
  );
}

function Change({ e, onOpen }: { e: AuditEventRow; onOpen: (e: AuditEventRow) => void }) {
  if (e.redacted) return <span className="text-muted">Withheld</span>;
  const n = new Set([...Object.keys(e.before ?? {}), ...Object.keys(e.after ?? {})]).size;
  const more = (e.details && Object.keys(e.details).length > 0) || Boolean(e.reason);
  if (n === 0 && !more) return <span className="text-muted">—</span>;
  return (
    <button type="button" onClick={() => onOpen(e)} className="inline-flex min-h-11 items-center rounded-lg px-2 text-accent underline underline-offset-2 hover:bg-canvas">
      {n > 0 ? `${n} ${n === 1 ? "field" : "fields"}` : "Details"}
    </button>
  );
}

export function AuditTrailPage() {
  const { session, config, me } = useAuth();
  const announce = useAnnounce();
  const [open, setOpen] = useState<AuditEventRow | null>(null);
  const formId = useId();
  const [filters, setFilters] = useState({ from: "", to: "", action: "", outcome: "" });
  const [applied, setApplied] = useState(filters);
  const [state, setState] = useState<{ events: AuditEventRow[]; nextCursor?: string; loading: boolean; error?: unknown }>({ events: [], loading: true });
  const [exporting, setExporting] = useState<{ busy: boolean; error?: unknown }>({ busy: false });
  const canExport = me?.permissions.includes("audit_event.export") ?? false;

  const query = useCallback(
    (cursor?: string) => {
      const q = new URLSearchParams();
      if (applied.from) q.set("from", new Date(applied.from).toISOString());
      if (applied.to) q.set("to", new Date(applied.to).toISOString());
      if (applied.action) q.set("action", applied.action);
      if (applied.outcome) q.set("outcome", applied.outcome);
      q.set("limit", "50");
      if (cursor) q.set("cursor", cursor);
      return `/v1/audit?${q.toString()}`;
    },
    [applied],
  );

  useEffect(() => {
    if (!session) return;
    let live = true;
    setState({ events: [], loading: true });
    fetchJson<AuditListResponse>(config, session, query())
      .then((r) => {
        if (!live) return;
        setState({ events: r.events, loading: false, ...(r.nextCursor ? { nextCursor: r.nextCursor } : {}) });
        announce(r.events.length === 0 ? "No audit events match." : `${r.events.length} audit events shown${r.nextCursor ? ", more available" : ""}.`);
      })
      .catch((error: unknown) => live && setState({ events: [], loading: false, error }));
    return () => {
      live = false;
    };
  }, [session, config, query, announce]);

  const loadMore = async () => {
    if (!session || !state.nextCursor) return;
    try {
      const r = await fetchJson<AuditListResponse>(config, session, query(state.nextCursor));
      setState((s) => ({ events: [...s.events, ...r.events], loading: false, ...(r.nextCursor ? { nextCursor: r.nextCursor } : {}) }));
      announce(`${r.events.length} more audit events loaded.`);
    } catch (error) {
      setState((s) => ({ ...s, error }));
    }
  };

  const exportCsv = async () => {
    if (!session) return;
    const from = applied.from ? new Date(applied.from) : new Date(Date.now() - 30 * 86_400_000);
    const to = applied.to ? new Date(applied.to) : new Date();
    setExporting({ busy: true });
    try {
      const path = `/v1/audit/export?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&format=csv`;
      const file = await fetchFile(config, session, path);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file.blob);
      a.download = file.filename.startsWith("audit-") ? file.filename : `audit-${from.toISOString().slice(0, 10)}-${to.toISOString().slice(0, 10)}.${file.contentType.includes("csv") ? "csv" : "json"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      announce("Export downloaded. The export itself is now in the trail.");
      setExporting({ busy: false });
    } catch (error) {
      setExporting({ busy: false, error });
    }
  };

  const columns = [
    { key: "when", header: "When", render: (e: AuditEventRow) => <RelativeTime iso={e.occurredAt} />, sortValue: (e: AuditEventRow) => e.occurredAt },
    { key: "action", header: "Action", render: (e: AuditEventRow) => <span title={e.action}>{actionLabel(e.action)}</span>, sortValue: (e: AuditEventRow) => actionLabel(e.action) },
    {
      key: "actor",
      header: "Who",
      render: (e: AuditEventRow) => {
        const who = actorLabel(e.actor);
        return (
          <span title={who.title}>
            {who.text}
            {who.qualifier && <span className="text-muted"> ({who.qualifier})</span>}
          </span>
        );
      },
      sortValue: (e: AuditEventRow) => actorLabel(e.actor).text,
    },
    {
      key: "resource",
      header: "Record",
      render: (e: AuditEventRow) => (
        <span>
          {e.resource.kind} <code className="font-mono text-xs text-muted" title={e.resource.id}>{e.resource.id.length > 12 ? `${e.resource.id.slice(0, 8)}…` : e.resource.id}</code>
        </span>
      ),
    },
    { key: "outcome", header: "Outcome", render: (e: AuditEventRow) => <StatusPill tone={OUTCOME_TONE[e.outcome]}>{OUTCOME_WORD[e.outcome]}</StatusPill>, sortValue: (e: AuditEventRow) => e.outcome },
    { key: "authority", header: "Authority", render: (e: AuditEventRow) => <StatusPill tone={AUTHORITY_TONE[e.authority]}>{AUTHORITY_WORD[e.authority]}</StatusPill> },
    { key: "change", header: "Change", render: (e: AuditEventRow) => <Change e={e} onOpen={setOpen} /> },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Program"
        title="Audit trail"
        subtitle="Every change of record and every refusal, newest first, within the programs you administer."
        actions={
          canExport ? (
            <Button onClick={() => void exportCsv()} disabled={exporting.busy}>
              {exporting.busy ? "Exporting…" : "Export"}
            </Button>
          ) : undefined
        }
        toolbar={
          <Card padding="sm" aria-label="Filter">
            <form
              aria-labelledby={`${formId}-legend`}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 [&>*]:min-w-0"
              onSubmit={(ev) => {
                ev.preventDefault();
                setApplied(filters);
              }}
            >
              <span id={`${formId}-legend`} className="sr-only">
                Filter the audit trail
              </span>
              <TextField label="From" type="date" value={filters.from} onChange={(ev) => setFilters({ ...filters, from: ev.target.value })} />
              <TextField label="To" type="date" value={filters.to} onChange={(ev) => setFilters({ ...filters, to: ev.target.value })} />
              <div className="grid gap-1">
                <label htmlFor={`${formId}-action`}>Action</label>
                <select id={`${formId}-action`} className="min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-surface px-3 text-base md:text-sm" value={filters.action} onChange={(ev) => setFilters({ ...filters, action: ev.target.value })}>
                  <option value="">Any</option>
                  {AUDIT_ACTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-1">
                <label htmlFor={`${formId}-outcome`}>Outcome</label>
                <select id={`${formId}-outcome`} className="min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-surface px-3 text-base md:text-sm" value={filters.outcome} onChange={(ev) => setFilters({ ...filters, outcome: ev.target.value })}>
                  <option value="">Any</option>
                  <option value="applied">Applied</option>
                  <option value="refused">Refused</option>
                  <option value="attempted">Attempted</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" variant="primary">
                  Apply filters
                </Button>
              </div>
            </form>
          </Card>
        }
      />
      {exporting.error ? <ErrorState title="The export failed" error={exporting.error} onRetry={() => void exportCsv()} /> : null}
      <div className="mt-6">
        {state.loading ? (
          <LoadingState label="Loading the audit trail…" />
        ) : state.error ? (
          <ErrorState title="The audit trail could not be loaded" error={state.error} onRetry={() => setApplied({ ...applied })} />
        ) : (
          <>
            <DataTable columns={columns} rows={state.events} rowKey={(e) => e.id} caption="Audit events" empty={<EmptyState title="No audit events" hint="Nothing matches these filters, or nothing has been recorded yet." />} initialSort={{ key: "when", dir: "descending" }} rowTestId={(e) => `audit-${e.id}`} />
            {state.nextCursor && (
              <p className="mt-4">
                <Button onClick={() => void loadMore()}>Load more</Button>
              </p>
            )}
          </>
        )}
      </div>
      {open && <ChangeDialog e={open} onClose={() => setOpen(null)} />}
      <p className="mt-6 text-xs text-muted">
        What this trail cannot show: changes made directly in the cloud account rather than through the product. Those are in the account's own CloudTrail, which the operator keeps. A read of a sensitive record appears here as its own event; the values of a change to one are withheld unless you hold the permission to read them.
      </p>
    </>
  );
}
